import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { AUTH } from "@/constants/testIds";
import { toast } from "sonner";
import { AuthShell, Field, Divider, GoogleIcon } from "./Login";
import { Building2, ChevronDown, ChevronUp } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function goGoogle() {
  const redirectUrl = window.location.origin + "/app";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Register() {
  const { lang } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const inviteToken = params.get("invite");
  const [inviteMeta, setInviteMeta] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [username, setUsername] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!inviteToken) return;
    (async () => {
      try {
        const { data } = await api.get(`/partner/invitations/verify?token=${inviteToken}`);
        setInviteMeta(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Invalid invitation");
      }
    })();
  }, [inviteToken]);

  const submit = async (e) => {
    e.preventDefault();
    if (!firstName || !lastName) { toast.error("Prénom et nom requis"); return; }
    if (password.length < 8) { toast.error("Mot de passe : min. 8 caractères"); return; }
    if (password !== confirm) { toast.error("Les mots de passe ne correspondent pas"); return; }
    if (!acceptTerms) { toast.error("Vous devez accepter les conditions"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/signup", {
        email, password, password_confirm: confirm,
        first_name: firstName, last_name: lastName,
        name: `${firstName} ${lastName}`.trim(),
        username: username || undefined,
        lang, invite: inviteToken || undefined,
        accept_terms: acceptTerms,
      });
      localStorage.setItem("altiora_token", data.access_token);
      await new Promise((r) => setTimeout(r, 100));
      window.location.href = "/app";
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let msg = "Inscription échouée";
      if (typeof detail === "string") {
        msg = detail;
      } else if (Array.isArray(detail) && detail.length) {
        // FastAPI validation errors — surface the first one
        const first = detail[0];
        const field = Array.isArray(first?.loc) ? first.loc.slice(-1)[0] : "";
        msg = `${field ? field + " : " : ""}${first?.msg || "champ invalide"}`;
      } else if (err?.message) {
        msg = `${err.message} (vérifiez votre connexion)`;
      }
      toast.error(msg, { duration: 8000 });
      console.error("[signup] error", err?.response?.status, err?.response?.data);
    } finally { setBusy(false); }
  };
  return (
    <AuthShell>
      <div className="text-[10px] font-semibold uppercase tracking-[0.32em]" style={{ color: "#C5A880" }}>
        ALTIORA · Rejoindre le mouvement
      </div>
      <h1 className="mt-4 font-serif font-medium text-3xl md:text-4xl tracking-tight text-[#0B2545] leading-tight">Créer un compte</h1>
      <p className="mt-2 text-sm text-[#5B5B62]">Rejoignez la plateforme qui orchestre votre trajectoire économique.</p>

      {inviteMeta && (
        <div className="mt-6 rounded-md border border-[#0B2545]/25 bg-[#0B2545]/5 px-4 py-3 flex items-center gap-3">
          <Building2 className="h-4 w-4 text-[#0B2545]" strokeWidth={1.5} />
          <div className="text-xs text-[#5B5B62]">
            {lang === "fr" ? "Invitation partenaire · " : "Partner invitation · "}
            <span className="text-[#1F2937] font-medium">{inviteMeta.org_name}</span>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="mt-8 space-y-4" noValidate>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Prénom">
            <input
              data-testid={AUTH.signupFirstName}
              type="text" required
              value={firstName} onChange={(e) => setFirstName(e.target.value)}
              className="w-full rounded-md bg-[#F8F7F3] border border-[#E4E1D8] focus:border-[#0B2545] focus:ring-1 focus:ring-[#0B2545] outline-none px-3 py-2.5 text-sm"
            />
          </Field>
          <Field label="Nom">
            <input
              data-testid={AUTH.signupLastName}
              type="text" required
              value={lastName} onChange={(e) => setLastName(e.target.value)}
              className="w-full rounded-md bg-[#F8F7F3] border border-[#E4E1D8] focus:border-[#0B2545] focus:ring-1 focus:ring-[#0B2545] outline-none px-3 py-2.5 text-sm"
            />
          </Field>
        </div>
        <Field label="Adresse courriel">
          <input
            data-testid={AUTH.signupEmail}
            type="email" required autoComplete="email"
            value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-md bg-[#F8F7F3] border border-[#E4E1D8] focus:border-[#0B2545] focus:ring-1 focus:ring-[#0B2545] outline-none px-3 py-2.5 text-sm"
          />
        </Field>

        <div>
          <button
            type="button"
            data-testid="signup-advanced-toggle"
            onClick={() => setShowAdvanced((v) => !v)}
            className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-[0.18em] text-[#5B5B62] hover:text-[#0B2545] transition-colors"
          >
            {showAdvanced ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            {showAdvanced ? "Masquer le pseudo (avancé)" : "Ajouter un pseudo (avancé)"}
          </button>
          {showAdvanced && (
            <div className="mt-3">
              <Field
                label="Nom d'utilisateur (facultatif)"
                error={username && !/^[a-zA-Z0-9_\-.]+$/.test(username) ? "Uniquement lettres, chiffres, . _ - (aucun espace)" : ""}
              >
                <input
                  data-testid={AUTH.signupUsername}
                  type="text" pattern="[a-zA-Z0-9_\-\.]+" minLength={3} maxLength={40}
                  value={username}
                  onChange={(e) => setUsername(e.target.value.trim().replace(/\s+/g, ""))}
                  className="w-full rounded-md bg-[#F8F7F3] border border-[#E4E1D8] focus:border-[#0B2545] focus:ring-1 focus:ring-[#0B2545] outline-none px-3 py-2.5 text-sm"
                  placeholder="ex : jsmith (pseudo unique, sans espace)"
                />
                <div className="mt-1 text-[11px] text-[#8B8B8F] leading-snug">
                  Pseudonyme public (comme sur X/Twitter). Laissez vide pour utiliser votre vrai nom.
                </div>
              </Field>
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Mot de passe">
            <input
              data-testid={AUTH.signupPassword}
              type="password" required minLength={8}
              value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-md bg-[#F8F7F3] border border-[#E4E1D8] focus:border-[#0B2545] focus:ring-1 focus:ring-[#0B2545] outline-none px-3 py-2.5 text-sm"
            />
          </Field>
          <Field label="Confirmer" error={confirm && password !== confirm ? "Ne correspond pas" : ""}>
            <input
              data-testid={AUTH.signupPasswordConfirm}
              type="password" required minLength={8}
              value={confirm} onChange={(e) => setConfirm(e.target.value)}
              className="w-full rounded-md bg-[#F8F7F3] border border-[#E4E1D8] focus:border-[#0B2545] focus:ring-1 focus:ring-[#0B2545] outline-none px-3 py-2.5 text-sm"
            />
          </Field>
        </div>
        <label className="flex items-start gap-2 text-xs text-[#1F2937]">
          <input
            type="checkbox"
            data-testid={AUTH.signupAcceptTerms}
            checked={acceptTerms}
            onChange={(e) => setAcceptTerms(e.target.checked)}
            className="mt-0.5 rounded border-[#E4E1D8] text-[#0B2545] focus:ring-[#0B2545]"
          />
          <span>
            J&apos;accepte les <a href="/terms" target="_blank" className="text-[#0B2545] underline">conditions d&apos;utilisation</a> et la <a href="/privacy" target="_blank" className="text-[#0B2545] underline">politique de confidentialité</a>.
          </span>
        </label>
        <button
          data-testid={AUTH.signupSubmit}
          type="submit" disabled={busy}
          className="w-full rounded-md bg-[#0B2545] hover:bg-[#123163] disabled:opacity-60 text-white font-medium py-2.5 transition-colors"
        >
          {busy ? "Création…" : "Créer mon compte"}
        </button>
      </form>

      <Divider label="OU" />

      <button
        onClick={goGoogle}
        className="w-full rounded-md border border-[#E4E1D8] hover:border-[#B8B5AC] text-[#1F2937] py-2.5 text-sm font-medium transition-colors flex items-center justify-center gap-2"
      >
        <GoogleIcon /> Continuer avec Google
      </button>

      <p className="mt-8 text-sm text-[#5B5B62] text-center">
        Déjà un compte&nbsp;?{" "}
        <Link to="/login" data-testid={AUTH.linkLogin} className="text-[#0B2545] hover:text-[#DCC29A] font-medium">
          Se connecter
        </Link>
      </p>
    </AuthShell>
  );
}
